import { useQuery } from '@tanstack/react-query';
import type { Paginated, PageContent, SiteBlock, SiteBlockType } from '@barbeiropro/contracts';
import { api } from '@/lib/api';

export interface PageSummary {
  id: string;
  title: string;
  slug: string;
  position: number;
  showInNav: boolean;
  draftVersion: number;
  published: boolean;
  publishedVersionId: string | null;
  publishedVersionNumber: number | null;
  updatedAt: string;
}
export interface PageVersion {
  id: string;
  versionNumber: number;
  createdAt: string;
  current: boolean;
}
export interface PageDetail extends PageSummary {
  draft: PageContent;
  versions: PageVersion[];
}
export interface Asset {
  id: string;
  url: string;
  alt: string;
  width: number | null;
  height: number | null;
  purpose: string;
}
export interface SiteSettings {
  primary: string;
  accent: string;
  background: string;
  fontPair: 'classic' | 'modern' | 'editorial';
  logoAssetId: string | null;
  faviconAssetId: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  publishedAt: string | null;
}
export interface ReviewRow {
  id: string;
  customerName: string;
  rating: number;
  comment: string;
  publicationAuthorized: boolean;
  createdAt: string;
}
export interface MessageRow {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  pageSlug: string | null;
  readAt: string | null;
  createdAt: string;
}

export const pagesKey = ['site', 'pages'] as const;
export const usePages = () => useQuery({ queryKey: pagesKey, queryFn: () => api.get<PageSummary[]>('/site/pages') });
export const useAssets = () => useQuery({ queryKey: ['assets', 'list'], queryFn: () => api.get<Asset[]>('/assets'), staleTime: 30_000 });
export const useServiceOptions = () =>
  useQuery({
    queryKey: ['catalog', 'services', 'options'],
    queryFn: () => api.get<Paginated<{ id: string; name: string; active: boolean; publicVisible: boolean }>>('/services', { pageSize: 100, sort: 'name' }),
    staleTime: 60_000,
  });

export const BLOCK_LABELS: Record<SiteBlockType, string> = {
  hero: 'Destaque (capa)',
  services: 'Serviços e preços',
  barbers: 'Equipe',
  gallery: 'Galeria de fotos',
  reviews: 'Avaliações',
  location: 'Localização e horários',
  cta: 'Chamada para agendar',
  text: 'Texto livre',
  faq: 'Perguntas frequentes',
  contact_form: 'Formulário de contato',
};

export function newBlock(type: SiteBlockType): SiteBlock {
  const id = crypto.randomUUID().slice(0, 8);
  const b = { id, hidden: false };
  switch (type) {
    case 'hero':
      return { ...b, type, eyebrow: '', title: 'Título da página', subtitle: '', primaryCta: { label: 'Agendar horário', href: '#agendar' } };
    case 'services':
      return { ...b, type, title: 'Serviços', subtitle: '', showPrices: true, serviceIds: [] };
    case 'barbers':
      return { ...b, type, title: 'Nossa equipe', subtitle: '', barberIds: [] };
    case 'gallery':
      return { ...b, type, title: 'Galeria', images: [] };
    case 'reviews':
      return { ...b, type, title: 'O que dizem nossos clientes', maxItems: 6 };
    case 'location':
      return { ...b, type, title: 'Onde estamos', branchId: null, showHours: true, showMap: true };
    case 'cta':
      return { ...b, type, title: 'Pronto para o próximo corte?', body: '', button: { label: 'Agendar agora', href: '#agendar' } };
    case 'text':
      return { ...b, type, title: '', body: '' };
    case 'faq':
      return { ...b, type, title: 'Perguntas frequentes', items: [{ q: 'Pergunta', a: 'Resposta' }] };
    case 'contact_form':
      return { ...b, type, title: 'Fale com a gente', subtitle: '', successMessage: 'Mensagem enviada. Retornaremos em breve.', successPageSlug: null };
  }
}

export function blockSummary(b: SiteBlock): string {
  switch (b.type) {
    case 'hero':
    case 'cta':
      return b.title;
    case 'services':
    case 'barbers':
    case 'gallery':
    case 'reviews':
    case 'location':
    case 'text':
    case 'faq':
    case 'contact_form':
      return b.title;
  }
}
